import * as XLSX from 'xlsx';
import {
  INVALID_COUPON_PRODUCT_ITEM_EXCEL_UPLOAD_CASE_TEXT,
  INVALID_COUPON_BRAND_ITEM_EXCEL_UPLOAD_CASE_TEXT,
  COUPON_EXCEL_CONSTANTS,
} from '../../constants/service/app/couponConstants';

/**
 * 불가능한 상품 항목
 */
export interface InvalidProductItem {
  productId?: number;
  productName?: string;
  storeName?: string;
  productInvalidType?: number;
  productIsInvalid: boolean;
}

/**
 * 불가능한 브랜드 항목
 */
export interface InvalidBrandItem {
  brandId?: number;
  storeName?: string;
  brandInvalidType?: number;
  brandIsInvalid: boolean;
}

/**
 * 엑셀 다운로드 타입
 */
export type ExcelDownloadType = 'product' | 'brand';

/**
 * 엑셀 설정
 */
interface ExcelConfig<T> {
  type: ExcelDownloadType;
  headers: string[];
  getItemId: (item: T) => number | undefined;
  getItemName: (item: T) => string | undefined;
  getStoreName?: (item: T) => string | undefined;
  getInvalidType: (item: T) => number | undefined;
  invalidTypeTextMap: Record<number, string>;
  fileNamePrefix: string;
}

/**
 * 타입에 따른 엑셀 설정 반환
 */
const getExcelConfig = <T extends InvalidProductItem | InvalidBrandItem>(type: ExcelDownloadType): ExcelConfig<T> => {
  if (type === 'product') {
    return {
      type: 'product',
      headers: ['상품 번호', '상품명', '브랜드명', '실패 사유'],
      getItemId: (item) => (item as InvalidProductItem).productId,
      getItemName: (item) => (item as InvalidProductItem).productName,
      getStoreName: (item) => (item as InvalidProductItem).storeName,
      getInvalidType: (item) => (item as InvalidProductItem).productInvalidType,
      invalidTypeTextMap: INVALID_COUPON_PRODUCT_ITEM_EXCEL_UPLOAD_CASE_TEXT,
      fileNamePrefix: '상품',
    };
  }

  return {
    type: 'brand',
    headers: ['브랜드 ID', '브랜드명', '실패 사유'],
    getItemId: (item) => (item as InvalidBrandItem).brandId,
    getItemName: (item) => (item as InvalidBrandItem).storeName,
    getInvalidType: (item) => (item as InvalidBrandItem).brandInvalidType,
    invalidTypeTextMap: INVALID_COUPON_BRAND_ITEM_EXCEL_UPLOAD_CASE_TEXT,
    fileNamePrefix: '브랜드',
  };
};

/**
 * 다운로드 파일명 생성
 */
const createFileName = (fileNamePrefix: string): string => {
  return `쿠폰_${fileNamePrefix}_불가능항목.xlsx`;
};

/**
 * 불가능 항목을 엑셀 행 데이터로 변환
 */
const mapItemToRow = <T>(item: T, config: ExcelConfig<T>): (string | number)[] => {
  const id = config.getItemId(item);
  const name = config.getItemName(item);
  const invalidType = config.getInvalidType(item);

  const message =
    (invalidType !== undefined ? config.invalidTypeTextMap[invalidType] : null) || COUPON_EXCEL_CONSTANTS.UNKNOWN_ERROR;

  if (config.type === 'product') {
    const storeName = config.getStoreName?.(item);
    return [
      id || COUPON_EXCEL_CONSTANTS.EMPTY_VALUE,
      name || COUPON_EXCEL_CONSTANTS.EMPTY_VALUE,
      storeName || COUPON_EXCEL_CONSTANTS.EMPTY_VALUE,
      message,
    ];
  }

  return [id || COUPON_EXCEL_CONSTANTS.EMPTY_VALUE, name || COUPON_EXCEL_CONSTANTS.EMPTY_VALUE, message];
};

/**
 * 불가능 항목을 엑셀로 다운로드
 * @param invalidList - 불가능 항목 리스트
 * @param type - 'product' 또는 'brand'
 */
export const downloadInvalidItemsExcel = (
  invalidList: InvalidProductItem[] | InvalidBrandItem[],
  type: ExcelDownloadType,
): void => {
  try {
    const config = getExcelConfig<InvalidProductItem | InvalidBrandItem>(type);
    const data = invalidList.map((item) => mapItemToRow(item, config));

    const worksheet = XLSX.utils.aoa_to_sheet([config.headers, ...data]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, COUPON_EXCEL_CONSTANTS.SHEET_NAME);

    const fileName = createFileName(config.fileNamePrefix);

    XLSX.writeFile(workbook, fileName);
  } catch (error) {
    alert('엑셀 다운로드에 실패했습니다. 다시 시도해주세요.');
  }
};
